import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { BLUEPRINT_CHECKOUT_URL, withUTM } from "@/config/blueprint";
import { SMS_MARKETING_LABEL, SMS_TRANSACTIONAL_LABEL, SMS_PHONE_DISCLOSURE } from "@/config/smsConsent";

// -----------------------------------------------------------------------------
// Blueprint pre-checkout lead modal
// -----------------------------------------------------------------------------
// Every purchase CTA on /blueprint opens this modal instead of jumping straight
// to Square. Flow: capture name/email/phone + UTMs + cta placement → POST to
// our backend → backend forwards to a dedicated GHL "checkout_started" webhook
// → we redirect the visitor into the Square checkout in the same tab.
//
// GHL failure never blocks the buyer: our backend responds 2xx even when the
// forward fails (the lead is persisted for retry), and the frontend always
// redirects on 2xx.
// -----------------------------------------------------------------------------

const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function readUtms() {
  const out = { utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "" };
  try {
    const stored = JSON.parse(sessionStorage.getItem("bcu_utm") || "{}");
    for (const k of utmKeys) if (stored[k]) out[k] = stored[k];
    // URL overrides sessionStorage on the current click
    const q = new URLSearchParams(window.location.search);
    for (const k of utmKeys) { const v = q.get(k); if (v) out[k] = v; }
  } catch { /* ignore */ }
  return out;
}

function validEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || ""); }
function validPhone(p) { return !p || /^[+()\-.\s\d]{7,}$/.test(p); }

export default function BlueprintLeadModal({ open, placement, onClose }) {
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    sms_marketing_consent: false, sms_transactional_consent: false,
    consent_privacy_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);

  // Focus first field, trap focus, lock body scroll, Escape to close
  useEffect(() => {
    if (!open) return;
    setError("");
    setBusy(false);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => firstInputRef.current?.focus(), 40);

    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setF((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // dedupe rapid clicks
    setError("");

    const first_name = f.first_name.trim();
    const last_name = f.last_name.trim();
    const email = f.email.trim();
    const phone = f.phone.trim();
    if (!first_name) { setError("Please enter your first name."); return; }
    if (!last_name) { setError("Please enter your last name."); return; }
    if (!validEmail(email)) { setError("Please enter a valid email."); return; }
    if (!validPhone(phone)) { setError("Please enter a valid phone number."); return; }
    if (!f.consent_privacy_terms) { setError("Please agree to the Terms & Conditions and Privacy Policy to continue."); return; }

    setBusy(true);
    const utms = readUtms();
    const payload = {
      first_name, last_name, email, phone,
      ...utms,
      cta_placement: placement || "unknown",
      page_url: typeof window !== "undefined" ? window.location.href : "",
      referrer: typeof document !== "undefined" ? document.referrer || "" : "",
      // A2P 10DLC — independent, optional SMS consents captured pre-checkout.
      sms_marketing_consent: !!f.sms_marketing_consent,
      sms_transactional_consent: !!f.sms_transactional_consent,
      sms_consent_language_marketing: f.sms_marketing_consent ? SMS_MARKETING_LABEL : "",
      sms_consent_language_transactional: f.sms_transactional_consent ? SMS_TRANSACTIONAL_LABEL : "",
      consent_privacy_terms: !!f.consent_privacy_terms,
    };

    // Fire InitiateCheckout / begin_checkout on confirmed intent-to-pay
    try {
      const meta = { product: "Bounce City University Blueprint", value: 497, currency: "USD", placement };
      if (typeof window.fbq === "function") window.fbq("track", "InitiateCheckout", meta);
      if (typeof window.gtag === "function") window.gtag("event", "begin_checkout", meta);
    } catch { /* ignore */ }

    let ok = false;
    try {
      const { status } = await api.post("/blueprint/checkout-lead", payload);
      ok = status >= 200 && status < 300;
    } catch (err) {
      // Backend hard error — do NOT strand the buyer. Continue to Square.
      // The lead is lost in this rare case; log it via console for triage.
      // eslint-disable-next-line no-console
      console.warn("Blueprint lead POST failed; continuing to Square:", err?.message || err);
      ok = false;
    }

    // Same-tab redirect — smoother than a second tab from inside the modal.
    const dest = withUTM(BLUEPRINT_CHECKOUT_URL);
    if (dest && !dest.startsWith("#")) {
      window.location.href = dest;
      return;
    }
    // Placeholder URL only (should not happen in prod)
    setBusy(false);
    if (!ok) setError("Something went wrong. Please try again.");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bp-lead-title"
      data-testid="blueprint-lead-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3 sm:p-6"
    >
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
      />
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg rounded-xl border border-[#F7931E]/40 bg-[#0A0A0A] p-6 sm:p-8 shadow-[0_35px_80px_-25px_rgba(247,147,30,0.45)]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="bp-lead-close"
          className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded border border-[rgba(255,255,255,0.16)] text-white/80 hover:text-white hover:border-[#F7931E]"
        >
          <X className="h-4 w-4" />
        </button>

        <p className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">The Bounce City University Blueprint</p>
        <h2 id="bp-lead-title" className="mt-3 font-display text-2xl sm:text-3xl leading-[1.1] text-white">
          Where Should We Send Your Course Access?
        </h2>
        <p className="mt-3 text-[14px] text-[#A9A9A9] leading-relaxed">
          Enter your details below to continue to the $497 Bounce City University Blueprint checkout.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="bcu-label">First Name *</span>
              <input ref={firstInputRef} data-testid="bp-lead-first-name" autoComplete="given-name" className="bcu-input" value={f.first_name} onChange={set("first_name")} />
            </label>
            <label className="block">
              <span className="bcu-label">Last Name *</span>
              <input data-testid="bp-lead-last-name" autoComplete="family-name" className="bcu-input" value={f.last_name} onChange={set("last_name")} />
            </label>
          </div>
          <label className="block">
            <span className="bcu-label">Email *</span>
            <input data-testid="bp-lead-email" type="email" inputMode="email" autoComplete="email" className="bcu-input" value={f.email} onChange={set("email")} />
          </label>
          <label className="block">
            <span className="bcu-label">Phone Number</span>
            <input data-testid="bp-lead-phone" type="tel" inputMode="tel" autoComplete="tel" className="bcu-input" value={f.phone} onChange={set("phone")} />
          </label>

          {/* A2P 10DLC — independent, optional SMS consents. */}
          <div className="border border-[rgba(255,255,255,0.10)] rounded-md p-4 bg-[#0A0A0A]">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">SMS Preferences</div>
            <p className="mt-2 text-[11px] text-[#7A8296] leading-relaxed">{SMS_PHONE_DISCLOSURE}</p>
            <label className="mt-3 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="bp-lead-sms-transactional"
                checked={!!f.sms_transactional_consent}
                onChange={(e) => setF((d) => ({ ...d, sms_transactional_consent: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-[#F7931E]"
              />
              <span className="text-[12px] text-[#C7CFDD] leading-relaxed">{SMS_TRANSACTIONAL_LABEL}</span>
            </label>
            <label className="mt-3 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="bp-lead-sms-marketing"
                checked={!!f.sms_marketing_consent}
                onChange={(e) => setF((d) => ({ ...d, sms_marketing_consent: e.target.checked }))}
                className="mt-1 h-4 w-4 accent-[#F7931E]"
              />
              <span className="text-[12px] text-[#C7CFDD] leading-relaxed">{SMS_MARKETING_LABEL}</span>
            </label>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="bp-lead-consent-privacy-terms"
              checked={!!f.consent_privacy_terms}
              onChange={(e) => setF((d) => ({ ...d, consent_privacy_terms: e.target.checked }))}
              className="mt-1 h-4 w-4 accent-[#F7931E]"
            />
            <span className="text-[12px] text-[#C7CFDD] leading-relaxed">
              I agree to the <Link to="/terms" className="underline hover:text-white">Terms &amp; Conditions</Link> and <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>. <span className="text-[#F7931E]">*</span>
            </span>
          </label>

          {error && (
            <div data-testid="bp-lead-error" role="alert" className="text-[13px] text-[#F7931E]">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="bp-lead-submit"
            className="bcu-btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {busy ? "Continuing to Checkout…" : (<>Get the Course — $497 <ArrowRight className="h-4 w-4" /></>)}
          </button>
          <p className="text-[12px] text-[#7A7A7A] text-center leading-relaxed">
            You'll continue to our secure checkout after submitting.
          </p>
          <p className="text-[11px] text-[#5A5A5A] text-center leading-relaxed">
            The Privacy Policy and Terms &amp; Conditions consent above covers your submission.
          </p>
        </form>
      </div>
    </div>
  );
}
